import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { LocalStorage } from '../local-storage/local-storage.service';
import { environment } from '../../../../environments/environment';
import { UpdateProfilePayload, User } from '../../models/interfaces/user.interface';
import { Api } from '../api/api';
import { TokenDto } from '../auth/models/token.dto';
import { meMock, toUserFromMock } from '../../mocks/interfaces/me.mock';
import { mergeIamUser } from '../../utils/jwt-claims.util';

@Injectable({
  providedIn: 'root',
})
export class MeService {

  constructor(
    private readonly localStorage: LocalStorage,
  ) {}

  private readonly _me: WritableSignal<User | null> = signal(null);

  private readonly api: Api = new Api();

  //#region Real API / public entrypoints

  public async loadMe(): Promise<User | null> {
    if (environment.useFakeBackend) {
      return this.loadMeMocked();
    }

    const token = this.localStorage.getItem<TokenDto>(environment.keys.token);

    if (!token)
      return null;

    const user = await this.api.getAsync<User>(environment.routes.users.me).then(r => r.success ?? null);
    return this.hydrateUserRoles(user);
  }

  public getMe(): User | null {
    return this.localStorage.getItem<User>(environment.keys.user);
  }

  public async storageMe(): Promise<void> {
    if (environment.useFakeBackend) {
      return this.storageMeMocked();
    }

    try {
      const user = await this.loadMe();
      this.me = user;
    } catch {
      this.me = null;
    }
  }

  public async updateProfile(payload: UpdateProfilePayload): Promise<User | null> {
    if (environment.useFakeBackend) {
      return this.updateProfileMocked(payload);
    }

    const me = this.getMe();

    if (!me)
      return null;

    const user = await this.api.putAsync<User>(environment.routes.users.one(me.id), {
      ...payload,
      imageUrl: payload.imageUrl === undefined ? me.imageUrl : payload.imageUrl,
    });

    if (!user?.success)
      return null;

    const hydrated = this.hydrateUserRoles(user.success, me);
    this.me = hydrated;
    return hydrated;
  }

  public get me(): Signal<User | null> {
    return this._me.asReadonly();
  }

  public set me(user: User | null) {
    this.localStorage.setItem(environment.keys.user, user);
    this._me.set(user);
  }

  //#endregion

  //#region Fake Backend

  public async loadMeMocked(): Promise<User | null> {
    return this.getMe() ?? meMock;
  }

  public async storageMeMocked(email?: string | null): Promise<void> {
    if (email) {
      this.me = toUserFromMock(email);
      return;
    }

    this.me = this.getMe() ?? meMock;
  }

  public async updateProfileMocked(payload: UpdateProfilePayload): Promise<User | null> {
    const current = this.getMe() ?? meMock;

    const updated: User = {
      ...current,
      name: payload.name ?? current.name,
      email: payload.email ?? current.email,
      birthDate: payload.birthDate ?? current.birthDate,
      phone: payload.phone ?? current.phone,
      cpfCnpj: payload.cpfCnpj ?? current.cpfCnpj,
      institution: payload.institution ?? current.institution,
      imageUrl: payload.imageUrl === undefined ? current.imageUrl : (payload.imageUrl ?? undefined),
      updatedAt: new Date(),
    };

    this.me = updated;
    return updated;
  }

  //#endregion

  //#region Private Helpers

  private hydrateUserRoles(user: User | null, fallback?: User | null): User | null {
    const token = this.localStorage.getItem<TokenDto>(environment.keys.token);
    const hydrated = mergeIamUser(user, token?.token);

    if (!hydrated) {
      return null;
    }

    const roles = hydrated.roles?.length ? hydrated.roles : fallback?.roles;
    const groupIds = hydrated.groupIds?.length ? hydrated.groupIds : fallback?.groupIds;
    const context = hydrated.context ?? fallback?.context;

    return {
      ...hydrated,
      ...(roles?.length ? { roles } : {}),
      ...(groupIds?.length ? { groupIds } : {}),
      ...(context ? { context } : {}),
    };
  }

  //#endregion

}
